/**
 * Custom React hooks for common patterns
 */

import { useCallback, useEffect, useState } from 'react';
import { useRecordContext, useNotify } from 'react-admin';

export interface UseFetchAssociationOptions {
  resourceId?: number;
  endpoint: string;
  dependencies?: unknown[];
}

export interface UseFetchAssociationResult<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * Custom hook to fetch associated resources with refresh capability
 * Useful for many-to-many relationships or nested resources
 *
 * @example
 * const { data: groups, loading, refresh } = useFetchAssociation({
 *   resourceId: machineId,
 *   endpoint: `/api/machines/${machineId}/groups`
 * });
 */
export const useFetchAssociation = <T = unknown>({
  resourceId,
  endpoint,
  dependencies = [],
}: UseFetchAssociationOptions): UseFetchAssociationResult<T> => {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchData = useCallback(async () => {
    if (!resourceId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch data'));
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [resourceId, endpoint]);

  const refresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, refreshKey, ...dependencies]);

  return { data, loading, error, refresh };
};

/**
 * Custom hook to fetch associated resources using the current record context
 * Automatically uses the record from React Admin context
 *
 * @example
 * const { data: machines, loading, refresh } = useFetchRecordAssociation({
 *   getEndpoint: (id) => `/api/groups/${id}/machines`
 * });
 */
export const useFetchRecordAssociation = <T = unknown>({
  getEndpoint,
  dependencies = [],
}: {
  getEndpoint: (id: number) => string;
  dependencies?: unknown[];
}): UseFetchAssociationResult<T> => {
  const record = useRecordContext();
  const recordId = typeof record?.id === 'number' ? record.id : Number(record?.id);

  return useFetchAssociation<T>({
    resourceId: recordId,
    endpoint: recordId ? getEndpoint(recordId) : '',
    dependencies: [recordId, ...dependencies],
  });
};

export interface UseApiMutationOptions {
  method?: 'POST' | 'PUT' | 'DELETE';
  onSuccess?: (data: unknown) => void;
  onError?: (error: Error) => void;
}

/**
 * Custom hook to perform API mutations
 * Handles loading state, authentication, and notifications
 */
export const useApiMutation = (options: UseApiMutationOptions = {}) => {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();

  const mutate = useCallback(
    async (endpoint: string, body?: unknown) => {
      setLoading(true);
      try {
        const response = await fetch(endpoint, {
          method: options.method || 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: body ? JSON.stringify(body) : undefined,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json().catch(() => ({}));
        if (options.onSuccess) {
          options.onSuccess(data);
        }
        return data;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Mutation failed');
        if (options.onError) {
          options.onError(error);
        } else {
          notify(error.message, { type: 'error' });
        }
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [options, notify]
  );

  return { mutate, loading };
};

/**
 * Custom hook to get an authenticated fetch client
 */
export const useApiClient = () => {
  const fetchWithAuth = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    const headers = {
      ...options.headers,
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    };
    return fetch(endpoint, { ...options, headers });
  }, []);
  return fetchWithAuth;
};
